import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Network,
  Plus,
  ArrowRight,
  Copy,
  MailPlus,
} from 'lucide-react';
import { PageHeader, Section, SplitView } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  CardFooter,
  Button,
  Badge,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useCustomer } from '@/api/customer';
import { useContacts, useOrgStats, useOrgRole } from '@/api/organization';
import { useMyTeams, type Team } from '@/api/teams';
import { useMyProjects, type Project } from '@/api/projects';
import { CompactStat } from '@/pages/services/components/CompactStat';
import { ORG_BASE } from './organizations-service';
import { statusTone, titleCase, formatCurrency } from '@/pages/account/organization/shared';
import styles from './OrgOverview.module.css';

const TOP_N = 5;

/** KPI tile — a CompactStat wrapped in a flat card (the ratified KPI-tile shape). */
function KpiTile({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <Card flat>
      <CardBody>
        <CompactStat label={label} value={value} hint={hint} />
      </CardBody>
    </Card>
  );
}

/**
 * Organizations console overview landing. Orient-and-act home (Datadog/Linear
 * energy): KPI tiles + your-teams / your-projects lists + a quick-actions and
 * org-info rail. Replaces the read-only ProfileTab-as-overview. No activity API
 * yet (§7 G1) — the teams/projects lists carry orientation instead.
 */
export function OrgOverview() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { data: customer } = useCustomer();
  const { isReseller } = useOrgRole();

  // Contacts: a single generous page is enough to count members (has_login) for
  // typical orgs; a dedicated count endpoint is a backend follow-up (§7 G2).
  const { data: contactsData } = useContacts({ page: 1, pageSize: 100 });
  const contacts = contactsData?.results ?? [];
  const contactsTotal = contactsData?.pagination?.total ?? contacts.length;
  const membersCount = contacts.filter((c) => c.has_login).length;

  const { data: teams = [] } = useMyTeams();
  const { data: projects = [] } = useMyProjects();
  const { data: stats } = useOrgStats({ enabled: isReseller });

  const teamsOwned = teams.filter((t) => t.user_role === 'owner').length;
  const activeProjects = projects.filter((p) => p.status === 'active').length;
  const resourceCount = projects.reduce((sum, p) => sum + (p.resources_count ?? 0), 0);

  const orgName = customer?.contact_name || 'Your organization';
  const go = (slug: string) => navigate(`${ORG_BASE}/${slug}`);

  const copyOrgId = async () => {
    if (!customer) return;
    try {
      await navigator.clipboard.writeText(String(customer.customer_id));
      show({ tone: 'success', title: 'Organization ID copied' });
    } catch {
      show({ tone: 'danger', title: 'Could not copy' });
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Organization"
        icon={<LayoutDashboard size={22} />}
        title={orgName}
        description={
          customer
            ? `Org ID ${customer.customer_id}${customer.contact_industry ? ` · ${titleCase(customer.contact_industry)}` : ''}`
            : 'Organization overview'
        }
        actions={
          <Button variant="primary" startIcon={<MailPlus size={15} />} onClick={() => go('members')}>
            Invite member
          </Button>
        }
      />

      <Section style={{ marginBottom: 'var(--vn-space-4)' }}>
        <div className={styles.kpiGrid}>
          <KpiTile label="Members" value={membersCount} hint={`${contactsTotal} contact${contactsTotal === 1 ? '' : 's'}`} />
          <KpiTile label="Teams" value={teams.length} hint={teamsOwned ? `${teamsOwned} you own` : undefined} />
          <KpiTile label="Projects" value={projects.length} hint={activeProjects ? `${activeProjects} active` : undefined} />
          <KpiTile label="Resources" value={resourceCount} />
          {isReseller && (
            <>
              <KpiTile
                label="Customers"
                value={stats?.customer_count ?? '—'}
                hint={stats ? `${stats.active_customers} active` : undefined}
              />
              <KpiTile label="MRR" value={stats ? formatCurrency(stats.mrr) : '—'} />
            </>
          )}
        </div>
      </Section>

      <SplitView
        aside={
          <div className={styles.rail}>
            <Card flat>
              <CardHeader>
                <CardTitle>Quick actions</CardTitle>
              </CardHeader>
              <CardBody>
                <div className={styles.actions}>
                  <Button variant="secondary" size="sm" startIcon={<MailPlus size={15} />} onClick={() => go('members')}>
                    Invite member
                  </Button>
                  <Button variant="secondary" size="sm" startIcon={<Plus size={15} />} onClick={() => go('teams')}>
                    Create team
                  </Button>
                  <Button variant="secondary" size="sm" startIcon={<Plus size={15} />} onClick={() => go('projects')}>
                    Create project
                  </Button>
                  {isReseller && (
                    <Button variant="secondary" size="sm" startIcon={<Network size={15} />} onClick={() => go('sub-customers')}>
                      Add customer
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>

            <Card flat>
              <CardHeader>
                <CardTitle>Organization</CardTitle>
              </CardHeader>
              <CardBody>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Org ID</span>
                  <span className={styles.infoValue}>
                    <span>{customer?.customer_id ?? '—'}</span>
                    {customer && (
                      <Button variant="ghost" size="sm" aria-label="Copy organization ID" onClick={copyOrgId}>
                        <Copy size={14} />
                      </Button>
                    )}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Primary contact</span>
                  <span className={styles.infoValue}>
                    <span>{customer?.contact_email || '—'}</span>
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Plan</span>
                  <span className={styles.infoValue}>
                    <Badge tone={isReseller ? 'accent' : 'neutral'} appearance="subtle" size="sm">
                      {isReseller ? 'Reseller' : 'Standard'}
                    </Badge>
                  </span>
                </div>
              </CardBody>
            </Card>
          </div>
        }
      >
        <div className={styles.mainStack}>
          <OverviewList
            title="Your teams"
            icon={<Users size={18} />}
            emptyLabel="You're not in any teams yet."
            onViewAll={() => go('teams')}
            rows={teams.slice(0, TOP_N).map((t) => (
              <TeamRow key={t.id} team={t} onOpen={() => go(`teams/${t.id}`)} />
            ))}
            hasRows={teams.length > 0}
          />
          <OverviewList
            title="Your projects"
            icon={<FolderKanban size={18} />}
            emptyLabel="No projects yet."
            onViewAll={() => go('projects')}
            rows={projects.slice(0, TOP_N).map((p) => (
              <ProjectRow key={p.id} project={p} onOpen={() => go(`projects/${p.id}`)} />
            ))}
            hasRows={projects.length > 0}
          />
        </div>
      </SplitView>
    </>
  );
}

function OverviewList({
  title,
  icon,
  rows,
  hasRows,
  emptyLabel,
  onViewAll,
}: {
  title: string;
  icon: React.ReactNode;
  rows: React.ReactNode;
  hasRows: boolean;
  emptyLabel: string;
  onViewAll: () => void;
}) {
  return (
    <Card flat>
      <CardHeader actions={<span style={{ color: 'var(--vn-fg-muted)' }}>{icon}</span>}>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        {hasRows ? <div className={styles.list}>{rows}</div> : <div className={styles.emptyLine}>{emptyLabel}</div>}
      </CardBody>
      <CardFooter>
        <Button variant="ghost" size="sm" onClick={onViewAll} className={styles.cardLink}>
          View all <ArrowRight size={14} />
        </Button>
      </CardFooter>
    </Card>
  );
}

/** Enter/Space activate a clickable row, matching native button semantics. */
function onRowKey(e: React.KeyboardEvent, onOpen: () => void) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onOpen();
  }
}

function TeamRow({ team, onOpen }: { team: Team; onOpen: () => void }) {
  return (
    <div
      className={styles.listRow}
      onClick={onOpen}
      onKeyDown={(e) => onRowKey(e, onOpen)}
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.listLeading}>
        <span className={styles.listIcon}>
          <Users size={16} />
        </span>
        <div className={styles.listName}>
          <span className={styles.listTitle}>{team.team_name || '—'}</span>
          <span className={styles.listMeta}>
            {(team.members_count ?? 0)} member{(team.members_count ?? 0) === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      <div className={styles.listRight}>
        {team.user_role && (
          <Badge tone={team.user_role === 'owner' ? 'primary' : 'neutral'} appearance="subtle" size="sm">
            {titleCase(team.user_role)}
          </Badge>
        )}
        <ArrowRight size={14} style={{ color: 'var(--vn-fg-subtle)' }} />
      </div>
    </div>
  );
}

function ProjectRow({ project, onOpen }: { project: Project; onOpen: () => void }) {
  return (
    <div
      className={styles.listRow}
      onClick={onOpen}
      onKeyDown={(e) => onRowKey(e, onOpen)}
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.listLeading}>
        <span className={styles.listIcon}>
          <FolderKanban size={16} />
        </span>
        <div className={styles.listName}>
          <span className={styles.listTitle}>{project.name || '—'}</span>
          <span className={styles.listMeta}>
            {(project.teams_count ?? 0)} team{(project.teams_count ?? 0) === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      <div className={styles.listRight}>
        <StatusPill tone={statusTone(project.status)}>{titleCase(project.status)}</StatusPill>
        <ArrowRight size={14} style={{ color: 'var(--vn-fg-subtle)' }} />
      </div>
    </div>
  );
}

export default OrgOverview;
